import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Title = styled.h2`
  text-align: center;
  color: #333;
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 15px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: #4caf50;
    box-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
  }
`;

const Container = styled.section`
  padding: 30px 0 0;
  position: relative;
  width: 100%;
  min-height: 100vh;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    mix-blend-mode: soft-light;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 0 40px;
  position: relative;
  z-index: 2;
`;

const CardContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 8px auto 0;
  padding: 30px 20px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  padding: 40px;
  width: 100%;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      rgba(76, 175, 80, 0.1) 0%,
      rgba(33, 150, 243, 0.1) 30%,
      rgba(156, 39, 176, 0.1) 60%,
      rgba(255, 193, 7, 0.1) 100%
    );
    z-index: 0;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ImageWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 4/3;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04),
    inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, box-shadow;

  &:hover {
    transform: translateY(-8px) scale(1.01);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.06),
      inset 0 0 0 1px rgba(255, 255, 255, 0.3);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.1) 100%
    );
    z-index: 1;
    opacity: 0;
    transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  padding: 10px;

  ${(props) =>
    (props.$isFirstImage ||
      props.$isSecondImage ||
      props.$isThirdImage ||
      props.$isDeliveryBoy ||
      props.$isDedicatedTeam ||
      props.$isDailyPayment) &&
    `
    object-fit: cover;
    padding: 0;
  `}
`;

const FeatureCards = () => {
  const features = [
    {
      image:
        "https://d2fmfnhu23ddb1.cloudfront.net/dropship%2Fdsi-images%2F1741323660185-dropship_banners_02-06.jpg",
    },
    {
      image:
        "https://d2fmfnhu23ddb1.cloudfront.net/dropship%2Fdsi-images%2F1741323660169-dropship_banners_02-05.jpg",
    },
    {
      image:
        "https://d2fmfnhu23ddb1.cloudfront.net/dropship%2Fdsi-images%2F1741323660150-dropship_banners_02-04.jpg",
    },
    {
      image:
        "https://d2fmfnhu23ddb1.cloudfront.net/dropship%2Fdsi-images%2F1741323660108-dropship_banners_02-02.jpg",
    },
    {
      image:
        "https://d2fmfnhu23ddb1.cloudfront.net/dropship%2Fdsi-images%2F1741323660134-dropship_banners_02-03.jpg",
    },
    {
      image:
        "https://d2fmfnhu23ddb1.cloudfront.net/dropship%2Fdsi-images%2F1741323660068-dropship_banners_02-01.jpg",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <Container>
      <ContentWrapper>
        <Title>Why Choose Dropship India?</Title>
        <CardContainer>
          <Card
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <ImageGrid>
              {features.map((feature, index) => (
                <ImageWrapper
                  key={index}
                  whileHover={{
                    scale: 1.05,
                    transition: { duration: 0.3 },
                  }}
                >
                  <Image
                    src={feature.image}
                    alt={`Feature ${index + 1}`}
                    loading="lazy"
                    $isFirstImage={index === 0}
                    $isSecondImage={index === 1}
                    $isThirdImage={index === 2}
                    $isDedicatedTeam={index === 4}
                    $isDailyPayment={index === 5}
                  />
                </ImageWrapper>
              ))}
            </ImageGrid>
          </Card>
        </CardContainer>
      </ContentWrapper>
    </Container>
  );
};

export default FeatureCards;
